import React from 'react';
import { Schema } from 'hooks/projects';
import {
  LogicalCondition,
  LogicalRule,
  RelationalCondition,
  RelationalRule,
} from 'utils/rules';
import {
  Colors,
  Flex,
  Input,
  OptionType,
  Row,
  Select,
  Button,
} from '@cognite/cogs.js';
import { CascaderSelect } from 'components/CascaderSelect/CascaderSelect';
import {
  addNestedCondition,
  constructCleanConditions,
  deleteCondition,
  getConditionLabel,
  getConditionOptions,
  getLogicalOperatorsOptions,
  mapPropertiesToAttributes,
  updateCondition,
} from 'utils/project-ruleset';
import styled from 'styled-components/macro';
import { DefaultOptionType } from 'antd/lib/cascader';
import { FeatureFlags, useFeatureFlag } from 'utils/feature-flags';
import { defaultRelationalRule } from 'utils/nested-conditions';
import { ConditionOptionsMenu } from './ConditionOptionsMenu';
import {
  booleanDefaultOption,
  needPropertyComparisonOptions,
  getBooleanOptions,
  needBooleanOptions,
  needValueInputOptions,
  needBooleanCorrection,
  isRelationalAttribute,
  getValueDisplayed,
} from './helpers';

type Props = {
  condition: LogicalRule | RelationalRule;
  schemas: Schema[];
  root: LogicalRule;
  ancestors: number[];
  onRootUpdate?: (root: LogicalRule) => void;
  onMenuAddCondition?: VoidFunction;
  onMenuDeleteCondition?: VoidFunction;
  selectedSchema?: Schema;
  depth?: number;
  enabledPropertyComparison?: boolean;
  showLabels?: boolean;
  isFilter?: boolean;
};

export const ConditionItem = ({
  condition,
  schemas,
  root,
  onRootUpdate = () => {},
  onMenuAddCondition = () => {},
  onMenuDeleteCondition = () => {},
  ancestors,
  selectedSchema,
  depth = 0,
  enabledPropertyComparison = true,
  showLabels = false,
  isFilter = false,
}: Props) => {
  const isPropertyComparison =
    condition._type === 'RelationalRule' && condition.isPropertyComparison;

  const propertyOptionsLeft =
    (selectedSchema &&
      mapPropertiesToAttributes(
        selectedSchema,
        schemas || [],
        isPropertyComparison
      )) ||
    [];
  const propertyOptionsRight =
    (selectedSchema &&
      mapPropertiesToAttributes(selectedSchema, schemas || [], true)) ||
    [];

  const enabledEndsWithFlag = useFeatureFlag(FeatureFlags.enabledEndsWith);
  const enabledIsUniqueFlag = useFeatureFlag(FeatureFlags.enabledIsUnique);
  const booleanOptions = getBooleanOptions(condition);

  const relationalAttribute = isRelationalAttribute(condition);

  const enabledIsUnique =
    !!enabledIsUniqueFlag && !isFilter && !relationalAttribute;

  let enabledPropertyCom = true;
  if (condition._type === 'RelationalRule') {
    enabledPropertyCom = enabledPropertyComparison && !condition.isManyToMany;
  }

  const conditionOptions: OptionType<string>[] = getConditionOptions({
    isPropertyComparison:
      condition._type === 'RelationalRule' && !!condition.isPropertyComparison,
    isPureRelation:
      condition._type === 'RelationalRule' && !!condition.hasChildren,
    enabledEndsWith: !!enabledEndsWithFlag,
    enabledIsUnique,
    dataType: condition._type === 'RelationalRule' ? condition.fieldType : '',
    schema: selectedSchema,
  });

  const onPropertyChange = (value: DefaultOptionType[]) => {
    const isMany2Many = value.some((v) => v.isManyToMany);
    const nestedLevel = value.find((v) => v.isManyToMany)?.nestedLevel;

    const last = value[value.length - 1];
    const newRule = updateCondition(root, ancestors, {
      ...condition,
      ...{
        property: value.map((optionType) => optionType.value!).join('.'),
        hasChildren: !!last?.children?.length,
        fieldType: last?.type,
        isManyToMany: isMany2Many,
        nestedLevel,
      },
    });
    onRootUpdate(newRule);
  };

  const onTogglePropertyComparison = (val: boolean) => {
    if (condition._type !== 'RelationalRule') return;
    const newRule = updateCondition(root, ancestors, {
      ...condition,
      value: val ? '' : condition?.value,
      isPropertyComparison: val,
    });
    onRootUpdate(newRule);
  };

  const onConditionChange = (option: OptionType<RelationalCondition>) => {
    if (condition._type === 'RelationalRule') {
      let updatedValue = condition.value;

      if (needBooleanCorrection(condition, option.value)) {
        const [booleanValue] = updatedValue.split(',');
        updatedValue = booleanValue;
      }
      const newCondition = updateCondition(root, ancestors, {
        ...condition,
        condition: option.value as RelationalCondition,
        value: updatedValue,
      });
      onRootUpdate(newCondition);
    }
  };

  const onLogicalConditionChange = (
    option: OptionType<LogicalCondition>,
    index: number = 0
  ) => {
    if (condition._type !== 'LogicalRule') return;

    // When you have only 1 or 2 children that means you don't need to add nesting. Just change the condition.

    if (condition.children?.length <= 2) {
      const newCondition = updateCondition(root, ancestors, {
        ...condition,
        condition: option.value as LogicalCondition,
      });
      onRootUpdate(constructCleanConditions(newCondition) as LogicalRule);

      // When there are more than 2 children we need to nest the two children where change happened
    } else if (
      option.value === LogicalCondition.AND ||
      option.value === LogicalCondition.OR
    ) {
      const newCondition = addNestedCondition(
        root,
        ancestors,
        index,
        option.value
      );
      onRootUpdate(constructCleanConditions(newCondition) as LogicalRule);
    }
  };

  const onValueChange = (value: string) => {
    if (condition._type !== 'RelationalRule') return;
    const newCondition = updateCondition(root, ancestors, {
      ...condition,
      value,
    });
    onRootUpdate(newCondition);
  };

  const onAddCondition = () => {
    if (condition._type === 'RelationalRule') {
      onMenuAddCondition();
      return;
    }
    const newRelationalCondition: RelationalRule = defaultRelationalRule;
    const newCondition = updateCondition(root, ancestors, {
      ...condition,
      children: [...condition.children, newRelationalCondition],
    });
    onRootUpdate(newCondition);
  };

  const onDeleteCondition = (index: number) => {
    if (condition._type === 'RelationalRule') {
      onMenuDeleteCondition();
      return;
    }
    const newRule = deleteCondition(root, ancestors, index);
    onRootUpdate(newRule);
  };

  return (
    <>
      {condition._type === 'LogicalRule' && (
        <AlternateBackground counter={depth}>
          {condition.children.map((child, index) => (
            <React.Fragment key={index}>
              <Margins>
                <ConditionItem
                  schemas={schemas}
                  root={root}
                  ancestors={[...ancestors, index]}
                  selectedSchema={selectedSchema}
                  condition={child}
                  depth={depth + 1}
                  onRootUpdate={onRootUpdate}
                  onMenuAddCondition={onAddCondition}
                  onMenuDeleteCondition={() => onDeleteCondition(index)}
                  enabledPropertyComparison={enabledPropertyCom}
                  showLabels={depth === 0 && index === 0}
                  isFilter={isFilter}
                />
              </Margins>
              {index !== condition.children.length - 1 && (
                <SelectLogicalConditionWrapper>
                  <Select
                    closeMenuOnSelect
                    disableTyping
                    value={{
                      value: condition.condition,
                      label: condition.condition,
                    }}
                    options={getLogicalOperatorsOptions(condition, depth)}
                    onChange={(val: OptionType<LogicalCondition>) =>
                      onLogicalConditionChange(val, index)
                    }
                  />
                </SelectLogicalConditionWrapper>
              )}
            </React.Fragment>
          ))}
          <StyledButtonAdd
            onClick={() => onAddCondition()}
            icon="Add"
            aria-label="Add Condition"
          />
        </AlternateBackground>
      )}
      {condition._type === 'RelationalRule' && (
        <Flex gap={5} alignItems="flex-end">
          <RowStretched>
            {showLabels && (
              <>
                <Label>Attribute</Label>
                <Label>Condition</Label>
                <Label>Value</Label>
              </>
            )}
            <CascaderSelect
              multiple={false}
              options={propertyOptionsLeft}
              allowClear={false}
              value={
                condition.property
                  ? getValueDisplayed(
                      condition.property,
                      condition.isManyToMany
                    )
                  : ['Find attribute…']
              }
              changeOnSelect
              disabled={!propertyOptionsLeft?.length}
              expandTrigger="hover"
              getPopupContainer={(node) => node}
              displayRender={(label) => label.join('.')}
              onChange={(_, sv) => onPropertyChange(sv)}
              showSearch
            />
            <WhiteBg>
              <Select
                closeMenuOnSelect
                fullWidth
                placeholder="Condition"
                value={{
                  label: getConditionLabel(condition.condition),
                  value: condition.condition,
                }}
                onChange={(val: OptionType<RelationalCondition>) =>
                  onConditionChange(val)
                }
                options={conditionOptions}
              />
            </WhiteBg>
            {needPropertyComparisonOptions(condition) && (
              <CascaderSelect
                multiple={false}
                options={propertyOptionsRight}
                allowClear={false}
                value={
                  condition.value
                    ? condition?.value?.toString().split('.')
                    : ['Find attribute…']
                }
                changeOnSelect
                disabled={!propertyOptionsRight?.length}
                expandTrigger="hover"
                getPopupContainer={(node) => node}
                displayRender={(label) => label.join('.')}
                onChange={(_, selectedValues) =>
                  onValueChange(
                    selectedValues
                      .map((selectedValue) => selectedValue.value)
                      .join('.')
                  )
                }
                showSearch
              />
            )}
            {needValueInputOptions(condition) && (
              <Input
                required
                value={condition.value}
                onChange={(e) => onValueChange(e.target.value)}
                placeholder="Add value"
              />
            )}
            {needBooleanOptions(condition) && (
              <Select
                closeMenuOnSelect
                fullWidth
                placeholder="Boolean value"
                value={
                  condition.value === ''
                    ? booleanDefaultOption
                    : {
                        label: condition.value.toString(),
                        value: condition.value.toString(),
                      }
                }
                onChange={(option: OptionType<string>) =>
                  option.value && onValueChange(option.value)
                }
                options={booleanOptions}
              />
            )}
          </RowStretched>
          <ConditionOptionsMenu
            onAddCondition={onAddCondition}
            onDelete={() => onDeleteCondition(0)}
            onTogglePropertyComparison={onTogglePropertyComparison}
            showPropertyComparison={enabledPropertyCom}
            propertyComparison={!!condition.isPropertyComparison}
          />
        </Flex>
      )}
    </>
  );
};

const Margins = styled.div`
  margin: 8px 0;
`;

const AlternateBackground = styled.div.attrs(
  (props: { counter: number }) => props
)`
  border-radius: 10px;
  ${({ counter }) => {
    if (counter === 0) {
      return `
        padding-left: 1rem;
        border-left: 2px solid rgb(164, 178, 252);
        border-radius: 0;
      `;
    }
    if (counter % 2 === 0) {
      return `
        background-color: var(--cogs-midblue-8);
        padding: 8px 2rem;
        border: 1px solid var(--cogs-midblue-5);
      `;
    }
    return `
        background-color:  var(--cogs-midblue-7);
        padding: 8px 2rem;
        border: 1px solid var(--cogs-midblue-5);
      `;
  }};
`;

const WhiteBg = styled.div`
  background-color: white;
`;

const SelectLogicalConditionWrapper = styled.div`
  max-width: 100px;
  .cogs-select {
    .cogs-select__control {
      background-color: var(--cogs-greyscale-grey3);
    }
  }
  & * {
    border: none;
    font-weight: 500;
    color: var(--cogs-greyscale-grey9);
  }
`;

const StyledButtonAdd = styled(Button)`
  width: 36px;
  height: 36px;
  background-color: var(--cogs-greyscale-grey3);
`;

const Label = styled.span`
  font-size: 13px;
  font-weight: 500;
  margin-bottom: 0px;
  text-transform: capitalize;
  color: rgb(64, 64, 64);
`;

const RowStretched = styled(Row)`
  flex-grow: 1;
  .cogs-select {
    background-color: white;
  }
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)) !important;
`;
